import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import icons from "../../../assets/images/constantIcons";
import images from "../../../assets/images/constantImages";
import { useDispatch, useSelector } from "react-redux";
import { getCategoryDetails } from "./CategorySlice";
import { handleError } from "../../Common/ApiCommon/ApiService";
import { editItem } from "./ItemsSlice";

const EditItemsForms = ({ isOpen, onClose, itemData }) => {
    console.log("itemData", itemData);

    const [isToggle, setIsToggle] = useState(false);
    const dispatch = useDispatch();
    const { data: categories, loading } = useSelector((state) => state.categories);

    useEffect(() => {
        if (isOpen) {
            dispatch(getCategoryDetails());
        }
    }, [dispatch, isOpen]);

    const mealTypes = ["vegeterian", "Appetizer", "Soup"];

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            canteen_category_id: itemData?.canteen_category_id || "",
            name: itemData?.name || "",
            price: itemData?.price || "",
            meal_type: itemData?.meal_type || "",
            stock_available: itemData?.stock_available || false,
            image: itemData?.image || "",
            imagePreview: itemData?.image ? `https://headquater.pixoatic.com${itemData.image}` : "",
        },
        validationSchema: Yup.object({
            canteen_category_id: Yup.string().required("Category is required"),
            meal_type: Yup.string().required("Meal type is required"),
            name: Yup.string().required("Item name is required"),
            price: Yup.number().typeError("Price must be a number").positive("Must be positive").required("Price is required"),
            image: Yup.mixed().when("imagePreview", {
                is: (imagePreview) => !imagePreview,
                then: (schema) => schema.required("Product image is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        onSubmit: async (values) => {
            try {
                const formData = new FormData()
                if (values.image instanceof File) {
                    formData.append("item[image]", values.image);
                }
                formData.append("item[canteen_category_id]", values.canteen_category_id);
                formData.append("item[name]", values.name);
                formData.append("item[price]", values.price);
                formData.append("item[meal_type]", values.meal_type);
                formData.append("item[stock_available]", values.stock_available);
                const response = await dispatch(editItem({ id: itemData.id, formData })).unwrap();
                toast.success("Item updated successfully!", { duration: 2000 });
                onClose();
            } catch (errors) {
                handleError(errors);
            }
        },
    });

    useEffect(() => {
        setIsToggle(formik.values.stock_available);
    }, [formik.values.stock_available]);

    if (loading) return <p>Loading...</p>;

    return (
        isOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px] relative">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-left">Edit Item</h2>
                        <div
                            className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
                            onClick={onClose}
                        >
                            <icons.IoClose className="text-green700" />
                        </div>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="space-y-4 overflow-y-auto text-left">

                        {/* Category */}
                        <div className="text-G300">
                            <label className="block text-sm font-normal mb-2">Category</label>
                            <select
                                name="canteen_category_id"
                                value={formik.values.canteen_category_id}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="inputtext"
                            >
                                <option value="">Select a category</option>
                                {categories?.map((category) => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                            </select>
                            {formik.touched.canteen_category_id && formik.errors.canteen_category_id && (
                                <div className="text-red text-sm">{formik.errors.canteen_category_id}</div>
                            )}
                        </div>

                        {/* Name and Price */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="text-G300">
                                <label className="block text-sm font-normal mb-2">Item name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className="inputtext"
                                    placeholder="Sicilian pizza"
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <div className="text-red text-sm">{formik.errors.name}</div>
                                )}
                            </div>
                            <div className="text-G300">
                                <label className="block text-sm font-normal mb-2">Price</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                                    <input
                                        type="number"
                                        name="price"
                                        placeholder="15"
                                        value={formik.values.price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className="inputtext text-green"
                                        style={{ paddingLeft: "25px" }}
                                    />
                                </div>
                                {formik.touched.price && formik.errors.price && (
                                    <div className="text-red text-sm">{formik.errors.price}</div>
                                )}
                            </div>
                        </div>

                        {/* Meal Type */}
                        <div className="text-G300">
                            <label className="block text-sm font-normal mb-2">Meal Type</label>
                            <select
                                name="meal_type"
                                value={formik.values.meal_type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="inputtext"
                            >
                                <option value="">Select a type</option>
                                {mealTypes.map((type, i) => (
                                    <option key={i} value={type}>{type}</option>
                                ))}
                            </select>
                            {formik.touched.meal_type && formik.errors.meal_type && (
                                <div className="text-red text-sm">{formik.errors.meal_type}</div>
                            )}
                        </div>

                        {/* Image Upload */}
                        <div className="text-G300">
                            <label className="block text-sm font-normal mb-2">Product Image</label>
                            <div
                                className="flex gap-3 items-center cursor-pointer w-20"
                                onClick={() => document.getElementById('fileInput').click()}
                            >
                                <img
                                    src={
                                        formik.values.imagePreview?.startsWith("blob:")
                                            ? formik.values.imagePreview
                                            : formik.values.imagePreview || images.downloadimage
                                    }
                                    alt="Product Preview"
                                    className="w-12 h-12 object-cover rounded"
                                />
                                <p>Upload</p>
                            </div>
                            <input
                                id="fileInput"
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => {
                                    const file = e.currentTarget.files[0];
                                    if (file) {
                                        formik.setFieldValue("image", file);
                                        formik.setFieldValue("imagePreview", URL.createObjectURL(file));
                                        formik.validateField("image");
                                    }
                                }}
                            />
                            {formik.touched.image && formik.errors.image && (
                                <div className="text-red text-sm">{formik.errors.image}</div>
                            )}
                        </div>

                        {/* Toggle */}
                        <div className="text-G300">
                            <label className="block text-sm font-normal mb-2">Stock available</label>
                            <div className="cursor-pointer" onClick={() => formik.setFieldValue("stock_available", !formik.values.stock_available)}>
                                {formik.values.stock_available
                                    ? <img src={images.ontogleswitch} alt="ontogleswitch" />
                                    : <img src={images.offtogleswitch} alt="offtogleswitch" />}
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end space-x-4 pt-4">
                            <button
                                type="button"
                                onClick={() => {
                                    formik.resetForm();
                                    onClose();
                                }}
                                className="w-[75px] sm:w-[93px] text-sm py-2 text-center border border-green text-green rounded-lg"
                            >
                                Close
                            </button>
                            <button
                                type="submit"
                                className="w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg bg-green text-white"
                            >
                                Update
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        )
    );
};

export default EditItemsForms;
